import React, { useState, useEffect } from "react";
import { Grid, Modal, Title, Group, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  ValueItem,
  ValueAdd,
  IntervalItem,
} from "@components/shared/Intervals";
import { Counter } from "@components/shared";
import { getUuid } from "@util/getUuid";

export const intervalValues = {
  hourly: 1,
  daily: 2,
  weekly: 3,
  monthly: 4,
  yearly: 5,
  ever: 6,
  custom: 7,
};

export default function Intervals({
  dates,
  sectionTitle = "Intervals",
  title = "Interval",
  items = [],
  onChange,
}) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  const disabledIntervals = items.map((m) => m.interval);
  const formattedSectionTitle =
    sectionTitle === "Intervals" ? t("common:interval_plural") : sectionTitle;

  return (
    <div style={{ marginBottom: "2em" }}>
      <Group>
        <Title order={4}>{formattedSectionTitle}</Title>
        <Button
          size="xs"
          variant="light"
          radius="xl"
          onClick={() => setCreating(true)}
        >
          {t("common:create")}
        </Button>
      </Group>
      {items.length > 0 && (
        <Grid mt="xs">
          {items.map((item) => (
            <Grid.Col key={item.uuid} span={{ base: 12, md: 6 }}>
              <IntervalItem
                key={item.uuid}
                frequency={item.frequency}
                interval={item.interval}
                values={item.values}
                onRemoveClick={() =>
                  onChange([...items].filter((f) => f.uuid !== item.uuid))
                }
              />
            </Grid.Col>
          ))}
        </Grid>
      )}
      <Modal
        opened={creating}
        onClose={() => setCreating(false)}
        title={`${t("common:create")} ${title}`}
      >
        <AddForm
          dates={dates}
          disabledIntervals={disabledIntervals}
          onSubmit={(e) => {
            onChange([...items, e]);
            setCreating(false);
          }}
        />
      </Modal>
    </div>
  );
}

export const AddForm = ({
  selectedInterval = null,
  disabledIntervals = [],
  hiddenIntervals = [],
  onSubmit,
  dates,
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({
    frequency: 0,
    interval: intervalValues.hourly,
    values: [],
  });

  useEffect(() => {
    if (selectedInterval) {
      setFormValues({
        ...formValues,
        interval: selectedInterval,
      });
    }
  }, [selectedInterval]);

  function onSubmitClick() {
    onSubmit({ ...formValues, uuid: getUuid() });
  }

  return (
    <div>
      {!selectedInterval && (
        <IntervalButtons
          value={formValues.interval}
          disabledIntervals={disabledIntervals}
          hiddenIntervals={hiddenIntervals}
          onChange={(e) =>
            setFormValues({
              ...formValues,
              interval: e,
              frequency: e === intervalValues.custom ? 0 : formValues.frequency,
            })
          }
        />
      )}
      <Group mt="lg" mb="lg">
        {formValues.interval !== intervalValues.custom && (
          <React.Fragment>
            <Counter
              value={formValues.frequency}
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  frequency: e,
                })
              }
            />
            <h3 style={{ margin: "0" }}>{t("common:times")}</h3>
          </React.Fragment>
        )}
      </Group>
      {formValues.interval === intervalValues.custom && (
        <React.Fragment>
          <h4 style={{ marginBottom: "0" }}>{t("common:values")}</h4>
          {formValues.values.map((v) => (
            <ValueItem
              key={v.uuid}
              day={v.day}
              start={v.start}
              end={v.end}
              onRemoveClick={() =>
                setFormValues({
                  ...formValues,
                  values: [...formValues.values].filter(
                    (f) => f.uuid !== v.uuid
                  ),
                })
              }
            />
          ))}
          <ValueAdd
            dates={dates}
            onAdd={(e) =>
              setFormValues({
                ...formValues,
                values: [...formValues.values, e],
              })
            }
          />
        </React.Fragment>
      )}
      <Button fullWidth mt="md" onClick={onSubmitClick}>
        {t("common:create")}
      </Button>
    </div>
  );
};

const IntervalButtons = ({
  value,
  disabledIntervals = [],
  hiddenIntervals = [],
  onChange,
}) => {
  const { t } = useTranslation();
  const intervalOptions = [
    { value: intervalValues.hourly, title: t("common:hour") },
    { value: intervalValues.daily, title: t("common:daily") },
    { value: intervalValues.weekly, title: t("common:weekly") },
    { value: intervalValues.monthly, title: t("common:monthly") },
    { value: intervalValues.yearly, title: t("common:yearly") },
    { value: intervalValues.ever, title: t("common:ever") },
    { value: intervalValues.custom, title: t("common:custom") },
  ].map((m, i) => ({
    ...m,
    key: i + 1,
    text: m?.title.charAt(0).toUpperCase(),
  }));

  return (
    <Grid>
      {intervalOptions
        .filter((f) => !hiddenIntervals.includes(f.value))
        .map((m) => (
          <Grid.Col span={{ base: 12, md: 4 }} key={m.key}>
            <Button
              key={m.key}
              onClick={() => onChange(m.value)}
              variant={value === m.value ? "light" : "subtle"}
              size="xs"
              color={value === m.value ? "blue" : "gray"}
              title={m.title}
              fullWidth
              disabled={disabledIntervals.includes(m.value)}
            >
              {m.title}
            </Button>
          </Grid.Col>
        ))}
    </Grid>
  );
};
